(ns frontend.view.user-management.users
    (:require
      [common.entity :as entity]
      [common.localization :as loc]
      [common.modules :as modules]
      [common.roles :as roles]
      [common.teams :as teams]
      [common.util :as cu]
      [frontend.state.current-session :as session]
      [frontend.view.user-management.components :as components]
      [frontend.view.user-management.events :as events]
      [oops.core :as oops]
      [reagent.core :as r]
      [ui.components.grid.view :as grid]
      [ui.components.toggle.view :as toggle]
      [ui.util :as uu]))

(defn resolve-columns
  [state]
  (let [all-modules (modules/all-modules @session/tenant
                                         @session/tenant-plan)]
    (concat
      [{:children [{:headerName (loc/tr :account/full-name)
                    :sortable   true
                    :field      :account/full-name
                    :resizable  true
                    :pinned     "left"
                    :checkboxSelection true
                    :headerCheckboxSelection false}]}]
      [{:children [{:headerName        (loc/tr :account/email)
                    :sortable          true
                    :resizable         true
                    :width             250
                    :suppressSizeToFit true

                    :field             :account/email}
                   {:headerName (loc/tr :settings/active?)
                    :field      :settings/active?
                    :resizable  true
                    :sortable   true}
                   {:field        :settings/admin?
                    :cellClass    "flex items-center"
                    :resizable    true
                    :cellRenderer (fn [props]
                                    (let [data (js->clj (aget props "data") {:keywordize-keys true})]
                                      (r/as-element
                                        (components/toggle-admin-cell-renderer events/toggle-role
                                                                               {:cell-value (events/get-cell-value props)
                                                                                :state      state
                                                                                :row-data   data}))))
                    :sortable     true}]}]
      [{:headerName (loc/tr :settings/teams-heading)
        :children   (->> @session/teams
                         (sort-by :team/name)
                         (map (fn [team]
                                {:headerName   (:team/name team)
                                 :field        (events/team-key team)
                                 :resizable    true
                                 :cellClass    "flex items-center"
                                 :cellRenderer (fn [props]
                                                 (let [data (js->clj (aget props "data") {:keywordize-keys true})]
                                                   (r/as-element
                                                     (components/toggle-user-team-cell-renderer events/toggle-team-membership
                                                                                                team
                                                                                                {:column-key (events/team-key team)
                                                                                                 :cell-value (events/get-cell-value props)
                                                                                                 :state      state
                                                                                                 :row-data   data}))))
                                 :sortable     true}))
                         (doall))}]
      [{:headerName (loc/tr :settings/modules-heading)
        :children   (->> all-modules
                         (sort-by loc/tr)
                         (map (fn [module]
                                {:headerName   (loc/tr module)
                                 :field        module
                                 :resizable    true
                                 :sortable     true
                                 :cellRenderer (fn [props]
                                                 (let [value (events/get-cell-value props)]
                                                   (r/as-element
                                                     [:span
                                                      {:class "text-xl"}
                                                      (if value
                                                        [:i.fal.fa-check-circle]
                                                        [:i.fal.fa-times-circle])])))}))
                         (doall))}])))

(defn resolve-row-data
  [role]
  (let [account (get @session/entities (:role/subject role))
        tenant @session/tenant
        all-teams @session/teams
        admin? (roles/admin? (:role/role role))
        account-modules (modules/resolve-account-modules
                          {:tenant         tenant
                           :tenant-modules (:tenant/modules tenant)
                           :tenant-plan    @session/tenant-plan
                           :all-teams      all-teams
                           :user-account   account
                           :mobile?        (session/is-mobile?)
                           :owner?         admin?})]
    (when account
      (merge
        account
        {:account/full-name                   (entity/display-string account)
         :settings/active?                    (if (:common/disabled? role)
                                                :team-member.status/deactivated
                                                :team-member.status/active)
         :settings/admin?                     (roles/admin? (:role/role role))
         :settings/belongs-to-read-only-team? (->> (teams/resolve-account-teams account all-teams)
                                                   (some #{:team/read-only-team?}))
         :role/role-id                        (:db/id role)}
        (->> @session/teams
             (map (fn [team]
                    [(events/team-key team)
                     (boolean (some #{(:db/id account)} (:team/members team)))]))
             (into {}))
        (->> account-modules
             (map (fn [m]
                    [m (boolean m)]))
             (into {}))))))

(defn grid-top-offset-sentinel
  [top-offset-atom*]
  (let [ref* (atom nil)
        update-grid-height (fn []
                             (when-let [el @ref*]
                               (let [rect (oops/ocall el "getBoundingClientRect")
                                     offset-from-top (+ (oops/oget rect "top")
                                                        (oops/oget js/window "scrollY"))]
                                 (reset! top-offset-atom* offset-from-top))))]
    (r/create-class
      {:component-did-mount    (fn []
                                 (oops/ocall js/window "addEventListener" "resize" update-grid-height)
                                 (update-grid-height))
       :component-will-unmount (fn []
                                 (oops/ocall js/window "removeEventListener" "resize" update-grid-height))
       :reagent-render
       (fn []
         [:div
          {:ref #(reset! ref* %)}])})))

(defn view
  [state]
  (let [tenant @session/tenant
        tenant-id (:db/id tenant)
        local-state (r/atom {})
        grid-top-offset* (r/atom 310)                       ;; About the default height for the navbars above the grid
        on-selection-changed (fn [params]
                               (let [grid-api (aget params "api")
                                     selected-nodes-js (oops/ocall grid-api "getSelectedNodes")
                                     selected-nodes (js->clj selected-nodes-js :keywordize-keys true)
                                     selected-node (first selected-nodes)]
                                 (if selected-node
                                   (let [selected-node-data (aget selected-node "data")
                                         selected-node-id (:id (js->clj selected-node-data {:keywordize-keys true}))]
                                     (swap! state assoc :selected-account-id selected-node-id))
                                   (swap! state dissoc :selected-account-id))))

        column-defs (resolve-columns state)]
    (swap! state assoc :selected-tab :settings/users)
    (fn [state]
      (let [all-users-roles (->> @session/all-roles
                                 (remove (fn [r]
                                           (roles/is-guest-role? (:role/role r))))
                                 (cu/filter-by :role/target tenant-id)
                                 (doall))
            all-users (->> all-users-roles
                           (keep resolve-row-data)
                           (doall))
            resolved-accounts (->> all-users
                                   (events/filter-accounts-by-search state (:user-search @state))
                                   (events/filter-by-status state))
            update-search-term! (fn [value]
                                  (swap! state assoc :user-search value))]
        [:div
         {:class "py-4 px-4 flex flex-col gap-4"}
         [:div
          {:class "flex items-start gap-2 text-base-light-text-paler"}
          [:i
           {:class "pt-1 fas fa-info-circle"}]
          [:span (loc/tr :settings/users-notification)]
          [:span (loc/tr :settings/team-members-count [(count resolved-accounts)])]]

         [:div
          {:class "flex items-center gap-4"}
          [:div
           {:class "min-w-72 relative"}
           [components/search-bar (:user-search @state)
            update-search-term! state]
           [:i
            {:class "fal fa-search absolute top-2/4 -translate-y-2/4 right-3"}]]
          [:div
           {:class "flex items-center gap-3"}
           [toggle/toggle
            {:checked?  (:show-deactivated? @state)
             :on-change (fn [toggled?]
                          (swap! state assoc :show-deactivated? toggled?))}]

           [:span (loc/tr :show-deactivated-users)]]]

         (if (empty? resolved-accounts)
           [components/not-found-view]
           [:div
            {:style {"--users-grid-top-offset" (str @grid-top-offset* "px")}}
            [:div
             {:class (uu/cm "py-2 flex gap-3 text-base-light-text bg-base-light")}
             [components/user-tools
              {:external-state state
               :local-state    local-state}]]
            [grid/top-offset-sentinel
             {:set-top-offset (fn [top-offset]
                                (reset! grid-top-offset* top-offset))}]
            [:div
             ;; The containing page has padding of 16px plus some,
             ;; so put it like this so scrollbar should not appear on the page, only the table
             {:class "h-[max(200px,calc(100vh-var(--users-grid-top-offset)-16px-1px))]"}
             [grid/ag-grid-enterprise
              {:theme              grid/theme-quartz
               :columnDefs         column-defs
               :rowData            resolved-accounts
               :onGridReady        (fn [event]
                                     (swap! local-state assoc-in [:grid :ref] event))
               :defaultColDef      {:sortable true
                                    :editable false
                                    :menuTabs ["generalMenuTab"]}
               :getRowId           (fn [params]
                                     (-> params
                                         (oops/oget "data")
                                         (oops/oget "id")
                                         (str)))
               :getRowClass        (fn [js-params]
                                     (let [row-index (oops/oget js-params "rowIndex")
                                           last? (= row-index (dec (count resolved-accounts)))]
                                       (when last?
                                         "border-b-[var(--ag-border-color)]")))
               :rowSelection       {:mode "singleRow"
                                    :checkboxes false}
               :popupParent        (oops/oget js/document "body")
               :onSelectionChanged on-selection-changed}]]

            #_[table/view
               {:grid-id              "teams-table"
                :state-atom           local-state
                :column-defs          column-defs
                ;:row-data             (take  1 resolved-accounts)
                :row-data             row-data
                :default-col-def      {:sortable true
                                       :editable false
                                       :menuTabs ["generalMenuTab"]}
                :min-height           "250px"
                :auto-height?         false
                :get-row-class        (fn [js-params]
                                        (let [row-index (oops/oget js-params "rowIndex")
                                              last? (= row-index (dec (count row-data)))]
                                          (when last?
                                            "border-b-[var(--ag-border-color)]")))
                :width                "100%"
                :row-selection        {:mode "singleRow"}
                :popup-parent         (oops/oget js/document "body")
                :on-selection-changed on-selection-changed}]])]))))
